import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/api';
import { useModal } from './ModalContext';
import { useAuth } from './AuthContext';
import { icons } from '../config/icons';
import { FormInput, FormSelect, FormTextarea, FormToggle } from '../components/common/FormFields';
import Icon from '../components/common/Icon';
import { createResourceService } from '../services/api.service';
import moment from 'moment';

const CMSContext = createContext();

export const useCMS = () => {
    const context = useContext(CMSContext);
    if (!context) {
        throw new Error('useCMS must be used within a CMSProvider');
    }
    return context;
};

// --- Dynamic Data Grid copy/paste helpers ---
// Identifies a card generated by a Dynamic Data Grid (a .card-case container).
const isGridCard = (card) => (
    card && card.element_type === 'container' && card.properties && typeof card.properties.className === 'string' && card.properties.className.includes('card-case')
);

// Identifies a Dynamic Data Grid target (the grid container itself).
const isGridTarget = (data) => (
    data && data.element_type === 'dynamic_data_grid'
);

// Pulls the datasource record id out of a generated card (button href => /baseLink/{id}).
const extractRecordId = (card) => {
    const button = (card.elements || []).find((b) => b.element_type === 'button');
    if (button && button.properties && button.properties.href) {
        const m = String(button.properties.href).match(/(\d+)\s*$/);
        if (m) return parseInt(m[1], 10);
    }
    const image = (card.elements || []).find((b) => b.element_type === 'image');
    if (image && image.properties && image.properties.src) {
        const m = String(image.properties.src).match(/\/(\d+)\/photo$/);
        if (m) return parseInt(m[1], 10);
    }
    return null;
};

const buildAscendedCard = async (sourceCard, gridData) => {
    const resource = (gridData.properties && gridData.properties.resource) || 'projects';
    const baseLink = (gridData.properties && gridData.properties.baseLink) || '/';
    const cardColumns = (gridData.properties && gridData.properties.cardColumns) || ['title', 'subtitle'];
    const cardColumnFormats = (gridData.properties && gridData.properties.cardColumnFormats) || {};
    const sourceId = extractRecordId(sourceCard);
    if (sourceId === null) return null;

    const service = createResourceService(resource);
    const response = await service.getAll({ limit: 1000, sortBy: 'id', sortOrder: 'asc', inactive: 0 });
    let records = response.data?.data || response.data || [];
    if (!Array.isArray(records)) records = [];

    const idx = records.findIndex((r) => String(r.id) === String(sourceId));
    const next = idx >= 0 ? records[idx + 1] : null;
    if (!next) return null;

    const newCard = JSON.parse(JSON.stringify(sourceCard));

    let textIndex = 0;
    (newCard.elements || []).forEach((element) => {
        if (element.element_type === 'image' && element.properties) {
            element.properties.src = next.coverImage || next.photoUrl || '/defaults/no-image.webp';
        } else if (element.element_type === 'text') {
            const col = cardColumns[textIndex];

            if (element.content && typeof element.content === 'string' && element.content.includes('{{')) {
                const regex = /\{\{([^}]+)\}\}/g;
                element.content = element.content.replace(regex, (match, key) => {
                    const cleanKey = key.replace(/<[^>]*>?/gm, '').replace(/&nbsp;/g, ' ').trim();
                    const parts = cleanKey.split('|').map(p => p.trim());
                    const colName = parts[0];
                    const filter = parts.length > 1 ? parts[1] : null;

                    let formatStr = null;
                    if (filter) {
                        formatStr = filter;
                        if (filter.startsWith('date:')) {
                            formatStr = filter.substring(5).trim();
                        }
                    }

                    let val = next[colName];
                    if (val === undefined || val === null) val = '';

                    if (formatStr && val) {
                        val = moment(val).format(formatStr);
                    } else if (cardColumnFormats[colName] && val) {
                        val = moment(val).format(cardColumnFormats[colName]);
                    }
                    return val;
                });
            } else if (col) {
                let value = next[col] || '';
                if (textIndex === 0 && !value) value = next.title || next.name || 'Untitled';
                if (textIndex === 1 && !value) value = next.subtitle || next.remarks || '';

                if (cardColumnFormats[col] && next[col]) {
                    value = moment(next[col]).format(cardColumnFormats[col]);
                }
                element.content = value;
            }
            textIndex++;
        } else if (element.element_type === 'button' && element.properties) {
            element.properties.href = `${baseLink}${next.id}`;
        }
    });

    newCard.elements = (newCard.elements || []).filter(el => !(el.element_type === 'text' && (!el.content || String(el.content).trim() === '')));

    return newCard;
};

export const CMSProvider = ({ children }) => {
    const { isAuthenticated, user } = useAuth();
    const [pages, setPages] = useState([]);
    const [activePage, setActivePage] = useState(null);
    const [projects, setProjects] = useState([]);
    const [activeProject, setActiveProject] = useState(() => {
        const saved = localStorage.getItem('cms_active_project_id');
        return saved ? saved : null;
    });

    useEffect(() => {
        if (activeProject) {
            localStorage.setItem('cms_active_project_id', activeProject);
        } else {
            localStorage.removeItem('cms_active_project_id');
        }
    }, [activeProject]);

    const [themeStyles, setThemeStyles] = useState({});
    const [themeSettings, setThemeSettings] = useState({});
    const [isThemeDirty, setIsThemeDirty] = useState(false);

    // Apply project-specific theme colors dynamically
    const applyThemeSettings = useCallback((settings) => {
        const variables = [
            'primary_lightest', 'primary_lighter', 'primary_light', 'primary', 'primary_dark', 'primary_darker', 'primary_darkest', 'primary_color', 'primary_bg', 'primary_bg_light', 'primary_rgb',
            'secondary_lightest', 'secondary_lighter', 'secondary_light', 'secondary', 'secondary_dark', 'secondary_darker', 'secondary_darkest', 'secondary_color', 'secondary_bg', 'secondary_bg_light', 'secondary_rgb',
            'page_background', 'content_background', 'popup_background',
            'header_text', 'header_hover', 'header_pressed', 'header_background', 'footer_text', 'footer_hover', 'footer_pressed', 'footer_background',
            'heading_text', 'heading_text_background', 'paragraph_text', 'paragraph_text_background',
            'link_color', 'hovered_link_color', 'pressed_link_color'
        ];

        let cssString = '';

        const generateRules = (selector, themeData) => {
            if (!themeData) return '';
            let rules = '';
            variables.forEach(v => {
                if (themeData[v]) {
                    rules += `  --${v.replace(/_/g, '-')}: ${themeData[v]};\n`;
                }
            });
            if (themeData.font_size) {
                rules += `  --base-font-size: ${themeData.font_size};\n`;
                rules += `  --font-size: ${themeData.font_size};\n`;
            }
            if (themeData.font_family) {
                rules += `  --font-family: '${themeData.font_family}', sans-serif;\n`;
            }
            if (!rules) return '';
            return `${selector} {\n${rules}}\n`;
        };

        cssString += generateRules('.cms-webpage', settings);
        if (settings.responsive && settings.responsive.tablet) {
            cssString += generateRules('.cms-webpage.tablet, .cms-webpage.mobile', settings.responsive.tablet);
        }
        if (settings.responsive && settings.responsive.mobile) {
            cssString += generateRules('.cms-webpage.mobile', settings.responsive.mobile);
        }

        let styleEl = document.getElementById('cms-dynamic-theme');
        if (!styleEl) {
            styleEl = document.createElement('style');
            styleEl.id = 'cms-dynamic-theme';
            document.head.appendChild(styleEl);
        }
        styleEl.innerHTML = cssString;

        setThemeStyles({});
    }, []);

    useEffect(() => {
        if (activeProject && projects.length > 0) {
            const project = projects.find(p => String(p.id) === String(activeProject));
            if (project) {
                let settings = {};
                try {
                    settings = typeof project.settings_draft === 'string'
                        ? JSON.parse(project.settings_draft)
                        : (project.settings_draft || {});
                } catch (e) { }

                setThemeSettings(settings);
                setIsThemeDirty(false);
                return;
            }
        }

        // Remove variables if no project is active or matching, but load global theme for default site
        const fetchGlobalTheme = async () => {
            try {
                const response = await api.get('/api/web/settings');
                if (response.data && response.data.settings) {
                    const globalDraftStr = response.data.settings.global_theme_settings_draft;
                    if (globalDraftStr) {
                        const globalSettings = typeof globalDraftStr === 'string' ? JSON.parse(globalDraftStr) : globalDraftStr;
                        setThemeSettings(globalSettings);
                        setIsThemeDirty(false);
                        return;
                    }
                }
            } catch (error) {
                console.error("Error fetching global theme settings:", error);
            }
            setThemeSettings({});
            setIsThemeDirty(false);
        };
        fetchGlobalTheme();
    }, [activeProject, projects]);

    useEffect(() => {
        applyThemeSettings(themeSettings);
    }, [themeSettings, applyThemeSettings]);

    const { openPopup, closePopup, showAlert } = useModal();
    const [loading, setLoading] = useState(true);
    const [templates, setTemplates] = useState({ themes: [], sections: [], elements: [] });
    const [selectedElementState, setSelectedElementState] = useState({ type: null, id: null, data: null });

    const [pageData, setPageData] = useState({
        id: null,
        title: '',
        sections: []
    });

    // Tracks which page id the current page-data fetch request belongs to, so a
    // slow/out-of-order response for a previously selected page can never
    // overwrite the canvas with the wrong page's content.
    const pageDataRequestIdRef = useRef(null);

    // Monotonically increasing token used to ignore stale project-fetch responses
    // when the active project changes (so an older project's result can never
    // restore its pages/activePage over the currently selected project).
    const projectRequestIdRef = useRef(0);

    const [isDirty, setIsDirty] = useState(false);
    const [footerData, setFooterData] = useState(null);
    const [isFooterDirty, setIsFooterDirty] = useState(false);
    const [headerData, setHeaderData] = useState(null);
    const [isHeaderDirty, setIsHeaderDirty] = useState(false);

    const [navigations, setNavigations] = useState([]);

    const [clipboard, setClipboard] = useState(() => {
        try {
            const saved = localStorage.getItem('cms_clipboard');
            return saved ? JSON.parse(saved) : null;
        } catch (e) {
            return null;
        }
    });

    useEffect(() => {
        if (clipboard) {
            localStorage.setItem('cms_clipboard', JSON.stringify(clipboard));
        }
    }, [clipboard]);

    const getElementByPath = useCallback((sectionId, path) => {
        if (!path && path !== 0) return null;

        if (sectionId === 'footer') {
            if (!footerData) return null;
            if (path === 'footer' || String(path) === 'footer') return footerData;

            const indices = String(path).split('-').map(Number);
            let currentArr = footerData.content;
            let target = null;

            for (let i = 0; i < indices.length; i++) {
                const pIdx = indices[i];
                if (!currentArr) return null;
                const actualIdx = currentArr.findIndex((b, j) => (b && b._pathIdx !== undefined ? b._pathIdx === pIdx : j === pIdx));
                const idx = actualIdx !== -1 ? actualIdx : pIdx;
                if (!currentArr[idx]) return null;
                target = currentArr[idx];
                currentArr = target.elements;
            }
            return target;
        }

        if (sectionId === 'header') {
            if (!headerData) return null;
            if (path === 'header' || String(path) === 'header') return headerData;

            const indices = String(path).split('-').map(Number);
            let currentArr = headerData.content;
            let target = null;

            for (let i = 0; i < indices.length; i++) {
                const pIdx = indices[i];
                if (!currentArr) return null;
                const actualIdx = currentArr.findIndex((b, j) => (b && b._pathIdx !== undefined ? b._pathIdx === pIdx : j === pIdx));
                const idx = actualIdx !== -1 ? actualIdx : pIdx;
                if (!currentArr[idx]) return null;
                target = currentArr[idx];
                currentArr = target.elements;
            }
            return target;
        }

        const section = pageData.sections.find(s => String(s.id) === String(sectionId));
        if (!section) return null;

        if (path === sectionId || String(path) === String(sectionId)) return section;

        const indices = String(path).split('-').map(Number);
        let currentArr = section.elements;
        let target = null;

        for (let i = 0; i < indices.length; i++) {
            const pIdx = indices[i];
            if (!currentArr) return null;
            const actualIdx = currentArr.findIndex((b, j) => (b && b._pathIdx !== undefined ? b._pathIdx === pIdx : j === pIdx));
            const idx = actualIdx !== -1 ? actualIdx : pIdx;
            if (!currentArr[idx]) return null;
            target = currentArr[idx];
            currentArr = target.elements;
        }

        return target;
    }, [pageData, footerData, headerData]);

    // Derive data dynamically from pageData / footerData / headerData to prevent stale state issues
    const selectedElement = {
        ...selectedElementState,
        data: (selectedElementState.id && !['nav-link', 'nav-dropdown'].includes(selectedElementState.type))
            ? (getElementByPath(selectedElementState.sectionId, selectedElementState.id) || selectedElementState.data)
            : selectedElementState.data
    };

    // Refs to store the latest context states and view settings to prevent stale closures in global toolbar popups
    const pageDataRef = useRef(pageData);
    pageDataRef.current = pageData;

    const footerDataRef = useRef(footerData);
    footerDataRef.current = footerData;

    const headerDataRef = useRef(headerData);
    headerDataRef.current = headerData;

    const selectedElementRef = useRef(selectedElement);
    selectedElementRef.current = selectedElement;

    const setSelectedElement = setSelectedElementState;
    const [activeStyleState, setActiveStyleState] = useState('normal'); // 'normal', 'hover', 'active', 'focus'
    const activeStyleStateRef = useRef(activeStyleState);
    activeStyleStateRef.current = activeStyleState;

    useEffect(() => {
        setActiveStyleState('normal');
    }, [selectedElementState.id, selectedElementState.sectionId]);

    const [hasUnpublishedChanges, setHasUnpublishedChanges] = useState(false);
    const [history, setHistory] = useState([]);
    const [future, setFuture] = useState([]);

    const lastSaveTime = useRef(0);

    const saveHistory = useCallback(() => {
        const now = Date.now();
        if (now - lastSaveTime.current < 500) {
            lastSaveTime.current = now;
            return;
        }
        lastSaveTime.current = now;

        setIsDirty(true);
        setHistory(prev => {
            const currentState = {
                pageData: JSON.parse(JSON.stringify(pageData)),
                headerData: headerData ? JSON.parse(JSON.stringify(headerData)) : null,
                footerData: footerData ? JSON.parse(JSON.stringify(footerData)) : null,
                navigations: navigations ? JSON.parse(JSON.stringify(navigations)) : [],
                selectedElementState: selectedElementState ? JSON.parse(JSON.stringify(selectedElementState)) : null
            };
            const newHistory = [...prev, currentState];
            if (newHistory.length > 50) newHistory.shift();
            return newHistory;
        });
        setFuture([]);
    }, [pageData, headerData, footerData, navigations, selectedElementState]);

    const undo = useCallback(() => {
        if (history.length === 0) return;

        setFuture(prev => [{
            pageData: JSON.parse(JSON.stringify(pageData)),
            headerData: headerData ? JSON.parse(JSON.stringify(headerData)) : null,
            footerData: footerData ? JSON.parse(JSON.stringify(footerData)) : null,
            navigations: navigations ? JSON.parse(JSON.stringify(navigations)) : [],
            selectedElementState: selectedElementState ? JSON.parse(JSON.stringify(selectedElementState)) : null
        }, ...prev]);

        const prevState = history[history.length - 1];
        setHistory(prev => prev.slice(0, -1));

        setPageData(prevState.pageData);
        if (prevState.headerData !== undefined) setHeaderData(prevState.headerData);
        if (prevState.footerData !== undefined) setFooterData(prevState.footerData);
        if (prevState.navigations !== undefined) setNavigations(prevState.navigations);

        setSelectedElementState(prevState.selectedElementState || { type: null, id: null, data: null });
        setIsDirty(history.length - 1 > 0);
    }, [history, pageData, headerData, footerData, navigations, selectedElementState]);

    const redo = useCallback(() => {
        if (future.length === 0) return;

        setHistory(prev => [...prev, {
            pageData: JSON.parse(JSON.stringify(pageData)),
            headerData: headerData ? JSON.parse(JSON.stringify(headerData)) : null,
            footerData: footerData ? JSON.parse(JSON.stringify(footerData)) : null,
            navigations: navigations ? JSON.parse(JSON.stringify(navigations)) : [],
            selectedElementState: selectedElementState ? JSON.parse(JSON.stringify(selectedElementState)) : null
        }]);

        const nextState = future[0];
        setFuture(prev => prev.slice(1));

        setPageData(nextState.pageData);
        if (nextState.headerData !== undefined) setHeaderData(nextState.headerData);
        if (nextState.footerData !== undefined) setFooterData(nextState.footerData);
        if (nextState.navigations !== undefined) setNavigations(nextState.navigations);

        setSelectedElementState(nextState.selectedElementState || { type: null, id: null, data: null });
        setIsDirty(true);
    }, [future, pageData, headerData, footerData, navigations, selectedElementState]);

    const [view, setView] = useState(localStorage.getItem('cms_view') || 'laptop');

    const viewRef = useRef(view);
    viewRef.current = view;

    const fetchPages = useCallback(async () => {
        // Token guards against out-of-order responses: only the latest project's
        // request may update pages/activePage, so switching projects (even rapidly,
        // or before the previous project finished loading) always lands on the
        // currently selected project.
        const requestToken = ++projectRequestIdRef.current;
        try {
            // Fetch projects (web_projects tenant table) — _t busts the redis CRUD cache
            // so manually-inserted DB rows are always visible in the admin UI.
            // Admin CMS ("/cms") must only list admin/global websites (id_client = 0).
            // Clients ('kind' !== 'admin') must NOT send id_client: the server-side
            // tenantMiddleware already injects id_client = req.user.id for them.
            const projectParams = { limit: 1000, _t: Date.now() };
            if (user && user.kind === 'admin') projectParams.id_client = 0;
            const projResponse = await api.get('/api/web_projects', { params: projectParams });
            if (projectRequestIdRef.current !== requestToken) return; // stale project request

            // CRUD getAll returns { data, totalRecords } (no `success` flag), while the
            // public endpoint returns { success, data } — read .data.data defensively.
            const fetchedProjects = (projResponse.data && Array.isArray(projResponse.data.data)) ? projResponse.data.data : [];
            setProjects(fetchedProjects);

            const currentProjectId = activeProject || null;
            // Edge case: localStorage may still hold a client-owned project id (saved
            // before scoping). Fall back to the Default Site (null) so pages don't load
            // for a project that is no longer in the admin list.
            if (currentProjectId && !fetchedProjects.some(p => String(p.id) === String(currentProjectId))) {
                setActiveProject(null);
                localStorage.removeItem('cms_active_project_id');
            }

            const params = currentProjectId ? { id_project: currentProjectId, _t: Date.now() } : { _t: Date.now() };
            const response = await api.get('/api/cms/pages', { params });
            if (projectRequestIdRef.current !== requestToken) return; // stale project request

            if (response.data.success) {
                // Filter to ensure we only have pages for the current project (in case params={} returns all pages)
                const fetchedPages = response.data.data.filter(p =>
                    currentProjectId ? String(p.id_project) === String(currentProjectId) : !p.id_project
                );

                setPages(fetchedPages);

                const savedPageId = localStorage.getItem('cms_active_page_id');
                const savedPage = savedPageId ? fetchedPages.find(p => String(p.id) === savedPageId) : null;
                const home = savedPage || fetchedPages.find(p => p.slug === 'home') || fetchedPages[0];
                setActivePage(home || null);
            }
        } catch (error) {
            console.error('Failed to fetch CMS pages:', error);
        } finally {
            if (projectRequestIdRef.current === requestToken) setLoading(false);
        }
    }, [activeProject, user]);

    // The localStorage effect for cms_active_project_id is handled above near state initialization

    const fetchPageData = useCallback(async (pageId) => {
        if (!pageId) return;
        try {
            const response = await api.get(`/api/cms/pages/${pageId}`);
            // Guard against out-of-order responses: only apply the result if it
            // still belongs to the currently active page. Otherwise an older,
            // slower request for a previous page would clobber the canvas.
            if (pageDataRequestIdRef.current !== pageId) return;
            if (response.data.success) {
                setPageData(response.data.data);
                setIsDirty(false);
                setHasUnpublishedChanges(false);
                setHistory([]);
                setFuture([]);
            }
        } catch (error) {
            console.error('Failed to fetch page data:', error);
        }
    }, []);

    const fetchTemplates = useCallback(async () => {
        try {
            const response = await api.get('/api/cms/templates');
            if (response.data.success) {
                setTemplates(response.data.data);
            }
        } catch (error) {
            console.error('Failed to fetch templates:', error);
        }
    }, []);

    const fetchNavigations = useCallback(async (projectId = null) => {
        try {
            const response = await api.get('/api/cms/navigations', { params: { id_project: projectId, _t: Date.now() } });
            if (response.data.success) {
                setNavigations(response.data.data);
            }
        } catch (error) {
            console.error('Failed to fetch navigations:', error);
        }
    }, []);

    useEffect(() => {
        if (isAuthenticated) {
            fetchPages();
            fetchTemplates();
        }
    }, [isAuthenticated, fetchPages, fetchTemplates]);

    const fetchFooterData = useCallback(async (projectId = null) => {
        try {
            const response = await api.get('/api/cms/footers', { params: { id_project: projectId, _t: Date.now() } });
            if (response.data.success) {
                setFooterData(response.data.data);
                setIsFooterDirty(false);
            }
        } catch (error) {
            console.error('Failed to fetch footer data:', error);
        }
    }, []);

    const fetchHeaderData = useCallback(async (projectId = null) => {
        try {
            const response = await api.get('/api/cms/headers', { params: { id_project: projectId, _t: Date.now() } });
            if (response.data.success) {
                setHeaderData(response.data.data);
                setIsHeaderDirty(false);
            }
        } catch (error) {
            console.error('Failed to fetch header data:', error);
        }
    }, []);

    useEffect(() => {
        if (activePage?.id) {
            pageDataRequestIdRef.current = activePage.id;
            // Immediately clear the canvas so it never lingers on the previous
            // page's content while the newly selected page is being fetched.
            setPageData(prev => (prev && prev.id === activePage.id) ? prev : { id: activePage.id, title: activePage.title, sections: [] });
            // Drop any selection from the previous page so the toolbar/props
            // panel don't keep referencing a block/section that no longer exists.
            setSelectedElementState({ type: null, id: null, data: null });
            fetchPageData(activePage.id);
            if (isAuthenticated) {
                fetchFooterData(activePage.id_project);
                fetchHeaderData(activePage.id_project);
                fetchNavigations(activePage.id_project);
            }
            localStorage.setItem('cms_active_page_id', String(activePage.id));
        }
    }, [activePage, isAuthenticated, fetchPageData, fetchFooterData, fetchHeaderData, fetchNavigations]);

    const prevActiveProjectRef = useRef(activeProject);

    useEffect(() => {
        const projectChanged = prevActiveProjectRef.current !== activeProject;
        prevActiveProjectRef.current = activeProject;

        if (projectChanged) {
            // When the active project changes, drop the previous project's canvas
            // content immediately so it never lingers on a page that belongs to the
            // old project. (If the new project has no pages, activePage becomes null
            // and the canvas stays cleared rather than showing the old project's page.)
            setPageData(prev => (prev && !prev.id && !(prev.sections || []).length) ? prev : { id: null, title: '', sections: [] });
            setSelectedElementState({ type: null, id: null, data: null });
            localStorage.removeItem('cms_active_page_id');
        }

        if (isAuthenticated) {
            // Always fetch the project's header and footer (even if it currently has no pages)
            fetchFooterData(activeProject || null);
            fetchHeaderData(activeProject || null);
            fetchNavigations(activeProject || null);
        }
    }, [activeProject, isAuthenticated, fetchFooterData, fetchHeaderData, fetchNavigations]);

    useEffect(() => {
        localStorage.setItem('cms_view', view);
    }, [view]);

    const selectPage = (page) => {
        setActivePage(page);
    };

    const savePageDraft = useCallback(async (updatedData = pageData) => {
        if (!updatedData.id) return false;
        try {
            let hasNewSections = false;
            // Save page sections
            for (let i = 0; i < updatedData.sections.length; i++) {
                const section = updatedData.sections[i];
                if (String(section.id).startsWith('temp_')) {
                    const response = await api.post('/api/cms/sections', {
                        id_page: updatedData.id,
                        template: section.template,
                        section_name: section.section_name,
                        elements: section.elements,
                        settings: section.settings
                    });
                    if (response.data.success) {
                        section.id = response.data.data.id;
                        hasNewSections = true;
                    }
                } else {
                    await api.put(`/api/cms/sections/${section.id}`, {
                        elements: section.elements,
                        settings: section.settings,
                        section_name: section.section_name
                    });
                }
            }

            // Always save order of sections during draft saving to capture reordering
            const orderedIds = updatedData.sections.map(s => s.id).filter(id => !String(id).startsWith('temp_'));
            if (orderedIds.length > 0) {
                await api.put('/api/cms/sections/reorder', { orderedIds }).catch(err => console.error(err));
            }

            // Save footer draft if dirty
            if (isFooterDirty && footerData) {
                await api.put('/api/cms/footers', {
                    id: footerData.id,
                    name: footerData.name,
                    elements: footerData.content,
                    settings: footerData.settings
                });
                setIsFooterDirty(false);
            }
            // Save header draft if dirty
            if (isHeaderDirty && headerData) {
                await api.put('/api/cms/headers', {
                    id: headerData.id,
                    name: headerData.name,
                    elements: headerData.content,
                    settings: headerData.settings
                });
                setIsHeaderDirty(false);
            }

            // Save navigations
            if (navigations) {
                await api.put('/api/cms/navigations', { navigations, id_project: activeProject || 0 });
            }

            // Save project theme settings
            if (isThemeDirty) {
                if (activeProject) {
                    await api.put(`/api/web_projects/${activeProject}`, { settings_draft: JSON.stringify(themeSettings) });
                } else {
                    await api.put('/api/web/settings', { key: 'global_theme_settings_draft', value: JSON.stringify(themeSettings) });
                }
                setIsThemeDirty(false);
                // Also trigger a fetch of projects so the projects list gets updated Draft settings
                if (typeof fetchPages === 'function') {
                    // This will refresh the projects if needed, or we just rely on local state
                }
            }
            setIsDirty(false);
            setHasUnpublishedChanges(true);
            setHistory([]);
            setFuture([]);
            return true;
        } catch (error) {
            console.error('Failed to save draft:', error);
            return false;
        }
    }, [pageData, isFooterDirty, footerData, isHeaderDirty, headerData, navigations, isThemeDirty, themeSettings, activeProject]);

    const publishPage = async () => {
        if (!activePage?.id) {
            console.error('No active page to publish');
            return false;
        }
        try {
            // Persist all drafts (including resolving any temp_ section IDs)
            const draftSaved = await savePageDraft(pageData);
            if (!draftSaved) return false;

            const response = await api.post(`/api/cms/pages/${activePage.id}/publish`, {
                sections: pageData.sections
            });

            // Publish footer if loaded
            if (footerData) {
                await api.post(`/api/cms/footers/${footerData.id}/publish`);
                setIsFooterDirty(false);
            }

            // Publish header if loaded
            if (headerData) {
                await api.post(`/api/cms/headers/${headerData.id}/publish`);
                setIsHeaderDirty(false);
            }

            // Publish project theme settings
            if (themeSettings) {
                if (activeProject) {
                    await api.put(`/api/web_projects/${activeProject}`, { settings_live: JSON.stringify(themeSettings) });
                } else {
                    await api.put('/api/web/settings', { key: 'global_theme_settings_live', value: JSON.stringify(themeSettings) });
                }
            }

            // console.log('Publish API Response:', response.data);
            if (response.data.success) {
                await fetchPageData(activePage.id);
                await fetchFooterData(activePage.id_project);
                await fetchHeaderData(activePage.id_project);
                await fetchNavigations(activePage.id_project);
                setHasUnpublishedChanges(false);
                return true;
            }
        } catch (error) {
            console.error('Failed to publish page:', error);
        }
        return false;
    };

    const updateSection = (id, updates) => {
        saveHistory();
        setPageData(prev => {
            const newSections = prev.sections.map(section =>
                section.id === id ? { ...section, ...updates } : section
            );
            return { ...prev, sections: newSections };
        });
    };

    const updateSectionSetting = (sectionId, key, value) => {
        saveHistory();

        let finalKey = key;

        if (sectionId === 'footer') {
            setFooterData(prev => {
                const settings = { ...prev.settings };
                const isGlobalKey = ['hideDesktop', 'hideTablet', 'hideMobile', 'mobileNavStyle'].includes(key);

                if (viewRef.current === 'mobile' && !isGlobalKey) {
                    if (!settings.responsive) settings.responsive = {};
                    if (!settings.responsive.mobile) settings.responsive.mobile = {};
                    settings.responsive.mobile[finalKey] = typeof value === 'function' ? value(settings.responsive.mobile[finalKey]) : value;
                } else if (viewRef.current === 'tablet' && !isGlobalKey) {
                    if (!settings.responsive) settings.responsive = {};
                    if (!settings.responsive.tablet) settings.responsive.tablet = {};
                    settings.responsive.tablet[finalKey] = typeof value === 'function' ? value(settings.responsive.tablet[finalKey]) : value;
                } else {
                    settings[finalKey] = typeof value === 'function' ? value(settings[finalKey]) : value;
                }

                const updatedFooter = { ...prev, settings, has_changes: true };
                setIsFooterDirty(true);
                setHasUnpublishedChanges(true);
                if (selectedElementRef.current.type === 'footer') {
                    setSelectedElement(prevSelected => ({ ...prevSelected, data: updatedFooter }));
                }
                return updatedFooter;
            });
            return;
        }

        if (sectionId === 'header') {
            setHeaderData(prev => {
                const settings = { ...prev.settings };
                const isGlobalKey = ['hideDesktop', 'hideTablet', 'hideMobile', 'mobileNavStyle'].includes(key);

                if (viewRef.current === 'mobile' && !isGlobalKey) {
                    if (!settings.responsive) settings.responsive = {};
                    if (!settings.responsive.mobile) settings.responsive.mobile = {};
                    settings.responsive.mobile[finalKey] = typeof value === 'function' ? value(settings.responsive.mobile[finalKey]) : value;
                } else if (viewRef.current === 'tablet' && !isGlobalKey) {
                    if (!settings.responsive) settings.responsive = {};
                    if (!settings.responsive.tablet) settings.responsive.tablet = {};
                    settings.responsive.tablet[finalKey] = typeof value === 'function' ? value(settings.responsive.tablet[finalKey]) : value;
                } else {
                    settings[finalKey] = typeof value === 'function' ? value(settings[finalKey]) : value;
                }

                const updatedHeader = { ...prev, settings, has_changes: true };
                setIsHeaderDirty(true);
                setHasUnpublishedChanges(true);
                if (selectedElementRef.current.type === 'header') {
                    setSelectedElement(prevSelected => ({ ...prevSelected, data: updatedHeader }));
                } else if (selectedElementRef.current.type === 'logo' && finalKey === 'logoSettings') {
                    const lSettings = updatedHeader.settings.logoSettings || {};
                    const lResp = updatedHeader.settings.responsive || {};
                    setSelectedElement(prevSelected => ({
                        ...prevSelected, data: {
                            settings: lSettings,
                            responsive: {
                                tablet: { properties: lResp.tablet?.logoSettings || {} },
                                mobile: { properties: lResp.mobile?.logoSettings || {} }
                            }
                        }
                    }));
                } else if (selectedElementRef.current.type === 'nav' && finalKey === 'navSettings') {
                    const nSettings = updatedHeader.settings.navSettings || {};
                    const nResp = updatedHeader.settings.responsive || {};
                    setSelectedElement(prevSelected => ({
                        ...prevSelected, data: {
                            settings: nSettings,
                            responsive: {
                                tablet: { properties: nResp.tablet?.navSettings || {} },
                                mobile: { properties: nResp.mobile?.navSettings || {} }
                            }
                        }
                    }));
                } else if (selectedElementRef.current.type === 'nav-dropdown' && finalKey === 'dropdownSettings') {
                    const dSettings = updatedHeader.settings.dropdownSettings || {};
                    const dResp = updatedHeader.settings.responsive || {};
                    setSelectedElement(prevSelected => ({
                        ...prevSelected, data: {
                            settings: dSettings,
                            responsive: {
                                tablet: { properties: dResp.tablet?.dropdownSettings || {} },
                                mobile: { properties: dResp.mobile?.dropdownSettings || {} }
                            }
                        }
                    }));
                }
                return updatedHeader;
            });
            return;
        }

        setPageData(prev => {
            const newSections = prev.sections.map(section => {
                if (section.id !== sectionId) return section;

                const settings = { ...section.settings };
                const isGlobalKey = ['hideDesktop', 'hideTablet', 'hideMobile', 'mobileNavStyle'].includes(key);

                if (viewRef.current === 'mobile' && !isGlobalKey) {
                    if (!settings.responsive) settings.responsive = {};
                    if (!settings.responsive.mobile) settings.responsive.mobile = {};
                    settings.responsive.mobile[finalKey] = typeof value === 'function' ? value(settings.responsive.mobile[finalKey]) : value;
                } else if (viewRef.current === 'tablet' && !isGlobalKey) {
                    if (!settings.responsive) settings.responsive = {};
                    if (!settings.responsive.tablet) settings.responsive.tablet = {};
                    settings.responsive.tablet[finalKey] = typeof value === 'function' ? value(settings.responsive.tablet[finalKey]) : value;
                } else {
                    settings[finalKey] = typeof value === 'function' ? value(settings[finalKey]) : value;
                }

                const updatedSection = { ...section, settings, has_changes: true };

                // If this section is currently selected, update the selection state too
                if (selectedElementRef.current.id === sectionId && selectedElementRef.current.type === 'section') {
                    setSelectedElement(prev => ({ ...prev, data: updatedSection }));
                }

                return updatedSection;
            });
            return { ...prev, sections: newSections };
        });
    };

    const updateNestedElements = (elements, path, action) => {
        const indices = String(path).split('-').map(Number);
        const newElements = [...elements];

        let currentArr = newElements;

        for (let i = 0; i < indices.length - 1; i++) {
            const pIdx = indices[i];
            if (!currentArr) break;
            const actualIdx = currentArr.findIndex((b, j) => (b && b._pathIdx !== undefined ? b._pathIdx === pIdx : j === pIdx));
            const idx = actualIdx !== -1 ? actualIdx : pIdx;
            if (!currentArr[idx]) break;
            currentArr[idx] = { ...currentArr[idx], elements: [...(currentArr[idx].elements || [])] };
            currentArr = currentArr[idx].elements;
        }

        const pIdx = indices[indices.length - 1];
        let lastIdx = pIdx;
        if (currentArr) {
            const actualIdx = currentArr.findIndex((b, j) => (b && b._pathIdx !== undefined ? b._pathIdx === pIdx : j === pIdx));
            lastIdx = actualIdx !== -1 ? actualIdx : pIdx;
        }

        const result = action(currentArr, lastIdx);

        return { elements: newElements, updatedItem: result };
    };

    const updateNavigationProperty = (navId, key, value) => {
        saveHistory();

        let finalKey = key;

        setNavigations(prev => {
            return prev.map(nav => {
                if (String(nav.id) === String(navId)) {
                    const updatedNav = { ...nav, has_changes: true };

                    if (['label', 'url', 'parent_id'].includes(key)) {
                        updatedNav[key] = value;
                    } else if (key === 'href') {
                        updatedNav['url'] = value;
                    } else if (['hideDesktop', 'hideTablet', 'hideMobile', 'iconLeft', 'iconRight', 'iconGap', 'target', 'title', 'aria-label', 'className', 'animation', 'hoverScale'].includes(key)) {
                        const settings = { ...(nav.settings || {}) };
                        settings[finalKey] = value;
                        updatedNav.settings = settings;
                    } else if (viewRef.current === 'mobile') {
                        if (!updatedNav.responsive) updatedNav.responsive = {};
                        if (!updatedNav.responsive.mobile) updatedNav.responsive.mobile = {};
                        if (!updatedNav.responsive.mobile.settings) updatedNav.responsive.mobile.settings = {};
                        updatedNav.responsive.mobile.settings[finalKey] = value;
                    } else if (viewRef.current === 'tablet') {
                        if (!updatedNav.responsive) updatedNav.responsive = {};
                        if (!updatedNav.responsive.tablet) updatedNav.responsive.tablet = {};
                        if (!updatedNav.responsive.tablet.settings) updatedNav.responsive.tablet.settings = {};
                        updatedNav.responsive.tablet.settings[finalKey] = value;
                    } else {
                        const settings = { ...(nav.settings || {}) };
                        settings[finalKey] = value;
                        updatedNav.settings = settings;
                    }

                    if (selectedElementRef.current.id === navId) {
                        setSelectedElement(prevSelected => ({
                            ...prevSelected,
                            data: {
                                ...updatedNav,
                                properties: updatedNav.settings,
                                responsive: {
                                    mobile: { properties: updatedNav.responsive?.mobile?.settings || {} },
                                    tablet: { properties: updatedNav.responsive?.tablet?.settings || {} }
                                }
                            }
                        }));
                    }
                    return updatedNav;
                }
                return nav;
            });
        });
        setHasUnpublishedChanges(true);
    };

    const addNavigation = (parentId = null) => {
        saveHistory();
        const newNav = {
            id: 'new_' + Date.now() + Math.floor(Math.random() * 1000),
            label: 'New Link',
            url: '#',
            parent_id: parentId,
            settings: {}
        };
        setNavigations(prev => [...prev, newNav]);
        setHasUnpublishedChanges(true);
    };

    const removeNavigation = (id) => {
        saveHistory();
        setNavigations(prev => prev.filter(nav => String(nav.id) !== String(id) && String(nav.parent_id) !== String(id)));
        if (String(selectedElementRef.current.id) === String(id)) {
            setSelectedElement({ type: null, id: null, data: null });
        }
        setHasUnpublishedChanges(true);
    };

    const reorderNavigations = (draggedId, targetId, isTargetSubLink, targetParentId, position = 'after') => {
        saveHistory();
        setNavigations(prev => {
            const list = [...prev];
            const draggedIndex = list.findIndex(nav => String(nav.id) === String(draggedId));
            if (draggedIndex === -1) return prev;

            const draggedItem = { ...list[draggedIndex], has_changes: true };
            list.splice(draggedIndex, 1);

            const targetIndex = list.findIndex(nav => String(nav.id) === String(targetId));

            if (isTargetSubLink) {
                draggedItem.parent_id = targetParentId;
            } else {
                draggedItem.parent_id = null;
            }

            if (targetIndex !== -1) {
                if (position === 'before') {
                    list.splice(targetIndex, 0, draggedItem);
                } else {
                    list.splice(targetIndex + 1, 0, draggedItem);
                }
            } else {
                list.push(draggedItem);
            }

            return list;
        });
        setHasUnpublishedChanges(true);
    };

    const updateElementProperty = (sectionId, elementPath, key, value) => {
        saveHistory();

        let finalKey = key;
        const formDataKeys = ['tableName', 'columnName', 'successMessage', 'name', 'required', 'options', 'label', 'placeholder', 'rows', 'type'];

        if (sectionId === 'footer') {
            setFooterData(prev => {
                const { elements: newElements, updatedItem: element } = updateNestedElements(prev.content, elementPath, (arr, idx) => {
                    const element = { ...arr[idx] };
                    if (key === 'tag') {
                        element.tag = value;
                    } else if (key === 'elements') {
                        element.elements = value;
                    } else if (['hideDesktop', 'hideTablet', 'hideMobile', 'button-style', 'iconLeft', 'iconRight', 'iconGap', 'href', 'target', 'title', 'aria-label', 'className', 'src', 'animation', 'poster', 'controls', 'autoplay', 'loop', 'muted', 'object-fit', ...formDataKeys].includes(key)) {
                        element.properties = { ...element.properties, [finalKey]: value };
                    } else if (viewRef.current === 'mobile') {
                        if (!element.responsive) element.responsive = {};
                        if (!element.responsive.mobile) element.responsive.mobile = {};
                        if (!element.responsive.mobile.properties) element.responsive.mobile.properties = {};
                        element.responsive.mobile.properties[finalKey] = value;
                    } else if (viewRef.current === 'tablet') {
                        if (!element.responsive) element.responsive = {};
                        if (!element.responsive.tablet) element.responsive.tablet = {};
                        if (!element.responsive.tablet.properties) element.responsive.tablet.properties = {};
                        element.responsive.tablet.properties[finalKey] = value;
                    } else {
                        element.properties = { ...element.properties, [finalKey]: value };
                    }
                    arr[idx] = element;
                    return element;
                });
                setIsFooterDirty(true);

                setHasUnpublishedChanges(true);
                if (selectedElementRef.current.id === elementPath) {
                    setSelectedElement(prevSelected => ({ ...prevSelected, data: element }));
                }
                return { ...prev, content: newElements, has_changes: true };
            });
            return;
        }

        if (sectionId === 'header') {
            setHeaderData(prev => {
                const { elements: newElements, updatedItem: element } = updateNestedElements(prev.content, elementPath, (arr, idx) => {
                    const element = { ...arr[idx] };
                    if (key === 'tag') {
                        element.tag = value;
                    } else if (key === 'elements') {
                        element.elements = value;
                    } else if (['hideDesktop', 'hideTablet', 'hideMobile', 'button-style', 'iconLeft', 'iconRight', 'iconGap', 'href', 'target', 'title', 'aria-label', 'className', 'src', 'animation', 'poster', 'controls', 'autoplay', 'loop', 'muted', 'object-fit', ...formDataKeys].includes(key)) {
                        element.properties = { ...element.properties, [finalKey]: value };
                    } else if (viewRef.current === 'mobile') {
                        if (!element.responsive) element.responsive = {};
                        if (!element.responsive.mobile) element.responsive.mobile = {};
                        if (!element.responsive.mobile.properties) element.responsive.mobile.properties = {};
                        element.responsive.mobile.properties[finalKey] = value;
                    } else if (viewRef.current === 'tablet') {
                        if (!element.responsive) element.responsive = {};
                        if (!element.responsive.tablet) element.responsive.tablet = {};
                        if (!element.responsive.tablet.properties) element.responsive.tablet.properties = {};
                        element.responsive.tablet.properties[finalKey] = value;
                    } else {
                        element.properties = { ...element.properties, [finalKey]: value };
                    }
                    arr[idx] = element;
                    return element;
                });
                setIsHeaderDirty(true);

                setHasUnpublishedChanges(true);
                if (selectedElementRef.current.id === elementPath) {
                    setSelectedElement(prevSelected => ({ ...prevSelected, data: element }));
                }
                return { ...prev, content: newElements, has_changes: true };
            });
            return;
        }

        setPageData(prev => {
            const section = prev.sections.find(s => s.id === sectionId);
            if (!section) return prev;

            const { elements: newElements, updatedItem: element } = updateNestedElements(section.elements, elementPath, (arr, idx) => {
                const element = { ...arr[idx] };

                if (key === 'tag') {
                    element.tag = value;
                    if (element.responsive?.mobile?.properties?.tag) {
                        delete element.responsive.mobile.properties.tag;
                    }
                    if (element.responsive?.tablet?.properties?.tag) {
                        delete element.responsive.tablet.properties.tag;
                    }
                } else if (key === 'elements') {
                    element.elements = value;
                } else if (['hideDesktop', 'hideTablet', 'hideMobile', 'button-style', 'iconLeft', 'iconRight', 'iconGap', 'href', 'target', 'title', 'aria-label', 'className', 'src', 'animation', 'poster', 'controls', 'autoplay', 'loop', 'muted', 'object-fit', ...formDataKeys].includes(key)) {
                    element.properties = { ...element.properties, [finalKey]: value };
                } else if (viewRef.current === 'mobile') {

                    if (!element.responsive) element.responsive = {};
                    if (!element.responsive.mobile) element.responsive.mobile = {};
                    if (!element.responsive.mobile.properties) element.responsive.mobile.properties = {};
                    element.responsive.mobile.properties[finalKey] = value;
                } else if (viewRef.current === 'tablet') {
                    if (!element.responsive) element.responsive = {};
                    if (!element.responsive.tablet) element.responsive.tablet = {};
                    if (!element.responsive.tablet.properties) element.responsive.tablet.properties = {};
                    element.responsive.tablet.properties[finalKey] = value;
                } else {
                    const properties = { ...element.properties, [finalKey]: value };
                    element.properties = properties;
                }

                arr[idx] = element;
                return element;
            });

            if (selectedElementRef.current.sectionId === sectionId && selectedElementRef.current.id === elementPath) {
                setSelectedElement(prevSelected => ({
                    ...prevSelected,
                    data: element
                }));
            }

            const newSections = prev.sections.map(s =>
                s.id === sectionId ? { ...s, elements: newElements, has_changes: true } : s
            );
            return { ...prev, sections: newSections };
        });
    };


    const updateElementContent = (sectionId, elementPath, content) => {
        saveHistory();
        if (sectionId === 'footer') {
            setFooterData(prev => {
                const { elements: newElements, updatedItem: element } = updateNestedElements(prev.content, elementPath, (arr, idx) => {
                    const element = { ...arr[idx], content };
                    arr[idx] = element;
                    return element;
                });
                setIsFooterDirty(true);
                setHasUnpublishedChanges(true);
                if (selectedElement.id === elementPath) {
                    setSelectedElement(prevSelected => ({ ...prevSelected, data: element }));
                }
                return { ...prev, content: newElements, has_changes: true };
            });
            return;
        }

        if (sectionId === 'header') {
            setHeaderData(prev => {
                const { elements: newElements, updatedItem: element } = updateNestedElements(prev.content, elementPath, (arr, idx) => {
                    const element = { ...arr[idx], content };
                    arr[idx] = element;
                    return element;
                });
                setIsHeaderDirty(true);
                setHasUnpublishedChanges(true);
                if (selectedElement.id === elementPath) {
                    setSelectedElement(prevSelected => ({ ...prevSelected, data: element }));
                }
                return { ...prev, content: newElements, has_changes: true };
            });
            return;
        }

        setPageData(prev => {
            const newSections = prev.sections.map(section => {
                if (section.id !== sectionId) return section;

                const { elements: newElements, updatedItem: element } = updateNestedElements(section.elements, elementPath, (arr, idx) => {
                    const element = { ...arr[idx], content };
                    arr[idx] = element;
                    return element;
                });

                if (selectedElement.sectionId === sectionId && selectedElement.id === elementPath) {
                    setSelectedElement(prevSelected => ({
                        ...prevSelected,
                        data: element
                    }));
                }

                return { ...section, elements: newElements, has_changes: true };
            });
            return { ...prev, sections: newSections };
        });
    };

    const addElement = (elementTemplate) => {
        saveHistory();
        let sectionId = null;
        let containerPath = null;
        let newElementPath = null;

        if (selectedElementRef.current.type === 'section') {
            sectionId = selectedElementRef.current.id;
        } else if (selectedElementRef.current.sectionId) {
            sectionId = selectedElementRef.current.sectionId;
            if (['container', 'dynamic_data_grid', 'form_container', 'form_row'].includes(selectedElementRef.current.type)) {
                containerPath = selectedElementRef.current.id;
            }
        } else if (selectedElementRef.current.type === 'footer') {
            sectionId = 'footer';
            if (selectedElementRef.current.id !== 'footer') {
                containerPath = selectedElementRef.current.id;
            }
        } else if (selectedElementRef.current.type === 'header') {
            sectionId = 'header';
            if (selectedElementRef.current.id !== 'header') {
                containerPath = selectedElementRef.current.id;
            }
        }

        if (!sectionId) {
            if (pageDataRef.current.sections.length > 0) {
                sectionId = pageDataRef.current.sections[pageDataRef.current.sections.length - 1].id;
            } else {
                return;
            }
        }

        if (containerPath !== null) {
            const targetElement = getElementByPath(sectionId, containerPath);
            if (targetElement) {
                newElementPath = `${containerPath}-${(targetElement.elements || []).length}`;
            }
        } else {
            if (sectionId === 'footer') {
                newElementPath = `${(footerData?.content || []).length}`;
            } else if (sectionId === 'header') {
                newElementPath = `${(headerData?.content || []).length}`;
            } else {
                const targetSection = pageDataRef.current.sections.find(s => String(s.id) === String(sectionId));
                if (targetSection) {
                    newElementPath = `${(targetSection.elements || []).length}`;
                }
            }
        }

        const handlePostAdd = () => {
            if (newElementPath !== null) {
                setTimeout(() => {
                    setSelectedElementState({
                        type: elementTemplate.element_type || 'element',
                        id: newElementPath,
                        data: elementTemplate,
                        sectionId: sectionId
                    });

                    let attempts = 0;
                    const checkInterval = setInterval(() => {
                        attempts++;
                        if (attempts > 10) {
                            clearInterval(checkInterval);
                            return;
                        }

                        let selector = `.cms-el-path-${String(newElementPath).replace(/-/g, '_')}`;
                        if (sectionId === 'header') selector = `.cms-header ${selector}`;
                        else if (sectionId === 'footer') selector = `.cms-footer ${selector}`;
                        else selector = `.cms-sec-${sectionId} ${selector}`;

                        const el = document.querySelector(selector);
                        if (el) {
                            clearInterval(checkInterval);
                            const rect = el.getBoundingClientRect();
                            const isViewable = (
                                rect.top >= 0 &&
                                rect.left >= 0 &&
                                rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
                                rect.right <= (window.innerWidth || document.documentElement.clientWidth)
                            );

                            if (!isViewable) {
                                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }
                        }
                    }, 50);
                }, 50);
            }
        };

        if (sectionId === 'footer') {
            setFooterData(prev => {
                let newElements = [...(prev.content || [])];
                if (containerPath !== null) {
                    const { elements: res } = updateNestedElements(prev.content, containerPath, (arr, idx) => {
                        const container = { ...arr[idx] };
                        container.elements = [...(container.elements || []), { ...elementTemplate }];
                        arr[idx] = container;
                        return container;
                    });
                    newElements = res;
                } else {
                    newElements.push({ ...elementTemplate });
                }
                setIsFooterDirty(true);
                setHasUnpublishedChanges(true);
                return { ...prev, content: newElements, has_changes: true };
            });
            handlePostAdd();
            return;
        }

        if (sectionId === 'header') {
            setHeaderData(prev => {
                let newElements = [...(prev.content || [])];
                if (containerPath !== null) {
                    const { elements: res } = updateNestedElements(prev.content, containerPath, (arr, idx) => {
                        const container = { ...arr[idx] };
                        container.elements = [...(container.elements || []), { ...elementTemplate }];
                        arr[idx] = container;
                        return container;
                    });
                    newElements = res;
                } else {
                    newElements.push({ ...elementTemplate });
                }
                setIsHeaderDirty(true);
                setHasUnpublishedChanges(true);
                return { ...prev, content: newElements, has_changes: true };
            });
            handlePostAdd();
            return;
        }

        setPageData(prev => {
            const newSections = prev.sections.map(section => {
                if (section.id !== sectionId) return section;

                if (containerPath !== null) {
                    const { elements: newElements } = updateNestedElements(section.elements, containerPath, (arr, idx) => {
                        const container = { ...arr[idx] };
                        container.elements = [...(container.elements || []), { ...elementTemplate }];
                        arr[idx] = container;
                        return container;
                    });
                    return { ...section, elements: newElements, has_changes: true };
                }

                return {
                    ...section,
                    elements: [...(section.elements || []), { ...elementTemplate }],
                    has_changes: true
                };
            });
            return { ...prev, sections: newSections };
        });
        handlePostAdd();
    };

    const deleteElement = (sectionId, elementPath) => {
        saveHistory();
        if (sectionId === 'footer') {
            setFooterData(prev => {
                const { elements: newElements } = updateNestedElements(prev.content, elementPath, (arr, idx) => {
                    arr.splice(idx, 1);
                });
                setIsFooterDirty(true);
                setHasUnpublishedChanges(true);
                return { ...prev, content: newElements, has_changes: true };
            });
            setSelectedElement({ type: 'footer', id: 'footer', sectionId: 'footer', data: null });
            return;
        }

        if (sectionId === 'header') {
            setHeaderData(prev => {
                const { elements: newElements } = updateNestedElements(prev.content, elementPath, (arr, idx) => {
                    arr.splice(idx, 1);
                });
                setIsHeaderDirty(true);
                setHasUnpublishedChanges(true);
                return { ...prev, content: newElements, has_changes: true };
            });
            setSelectedElement({ type: 'header', id: 'header', sectionId: 'header', data: null });
            return;
        }

        setPageData(prev => {
            const newSections = prev.sections.map(section => {
                if (section.id !== sectionId) return section;

                const { elements: newElements } = updateNestedElements(section.elements, elementPath, (arr, idx) => {
                    arr.splice(idx, 1);
                });

                return { ...section, elements: newElements, has_changes: true };
            });
            return { ...prev, sections: newSections };
        });
        setSelectedElement({ type: 'section', id: sectionId, sectionId: sectionId, data: null });
    };

    const addColumnToRow = (sectionId, rowElementPath) => {
        saveHistory();

        let newElementPath = null;
        const targetElement = getElementByPath(sectionId, rowElementPath);
        
        let newColTemplate = { element_type: 'container', properties: { className: 'cms-col' }, elements: [] };
        
        if (targetElement) {
            if (targetElement.elements && targetElement.elements.length > 0) {
                const lastColumn = targetElement.elements[targetElement.elements.length - 1];
                newColTemplate = JSON.parse(JSON.stringify(lastColumn));
            }
            newElementPath = `${rowElementPath}-${(targetElement.elements || []).length}`;
        }

        const handlePostAdd = () => {
            if (newElementPath !== null) {
                setTimeout(() => {
                    setSelectedElementState({
                        type: 'container',
                        id: newElementPath,
                        data: newColTemplate,
                        sectionId: sectionId
                    });

                    let attempts = 0;
                    const checkInterval = setInterval(() => {
                        attempts++;
                        if (attempts > 10) {
                            clearInterval(checkInterval);
                            return;
                        }

                        let selector = `.cms-el-path-${String(newElementPath).replace(/-/g, '_')}`;
                        if (sectionId === 'header') selector = `.cms-header ${selector}`;
                        else if (sectionId === 'footer') selector = `.cms-footer ${selector}`;
                        else selector = `.cms-sec-${sectionId} ${selector}`;

                        const el = document.querySelector(selector);
                        if (el) {
                            clearInterval(checkInterval);
                            const rect = el.getBoundingClientRect();
                            const isViewable = (
                                rect.top >= 0 &&
                                rect.left >= 0 &&
                                rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
                                rect.right <= (window.innerWidth || document.documentElement.clientWidth)
                            );

                            if (!isViewable) {
                                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }
                        }
                    }, 50);
                }, 50);
            }
        };

        if (sectionId === 'footer') {
            setFooterData(prev => {
                const { elements: newElements } = updateNestedElements(prev.content, rowElementPath, (arr, idx) => {
                    const container = { ...arr[idx] };
                    container.elements = [...(container.elements || []), { ...newColTemplate }];
                    arr[idx] = container;
                });
                setIsFooterDirty(true);
                setHasUnpublishedChanges(true);
                return { ...prev, content: newElements, has_changes: true };
            });
            handlePostAdd();
            return;
        }

        if (sectionId === 'header') {
            setHeaderData(prev => {
                const { elements: newElements } = updateNestedElements(prev.content, rowElementPath, (arr, idx) => {
                    const container = { ...arr[idx] };
                    container.elements = [...(container.elements || []), { ...newColTemplate }];
                    arr[idx] = container;
                });
                setIsHeaderDirty(true);
                setHasUnpublishedChanges(true);
                return { ...prev, content: newElements, has_changes: true };
            });
            handlePostAdd();
            return;
        }

        setPageData(prev => {
            const newSections = prev.sections.map(section => {
                if (section.id !== sectionId) return section;

                const { elements: newElements } = updateNestedElements(section.elements, rowElementPath, (arr, idx) => {
                    const container = { ...arr[idx] };
                    container.elements = [...(container.elements || []), { ...newColTemplate }];
                    arr[idx] = container;
                });

                return { ...section, elements: newElements, has_changes: true };
            });
            return { ...prev, sections: newSections };
        });
        handlePostAdd();
    };

    const removeColumnFromRow = (sectionId, colElementPath) => {
        // Extract the parent row path and the column index from the column's block path
        const indices = String(colElementPath).split('-').map(Number);
        if (indices.length < 2) return false;

        const parentPath = indices.slice(0, -1).join('-');

        // Check if parent has more than 1 column (minimum guard)
        const section = sectionId === 'footer' ? footerDataRef.current :
            sectionId === 'header' ? headerDataRef.current :
                pageDataRef.current.sections.find(s => s.id === sectionId);
        if (!section) return false;

        const sourceElements = sectionId === 'footer' || sectionId === 'header' ?
            section.content : section.elements;

        const parentElement = (function findElement(elements, path) {
            const pIndices = String(path).split('-').map(Number);
            let current = elements;
            for (let i = 0; i < pIndices.length; i++) {
                if (!current || !current[pIndices[i]]) return null;
                if (i === pIndices.length - 1) return current[pIndices[i]];
                current = current[pIndices[i]].elements;
            }
            return null;
        })(sourceElements, parentPath);

        const childCount = parentElement?.elements?.length || 0;
        if (childCount <= 1) return false;

        // Proceed with removal
        saveHistory();

        if (sectionId === 'footer') {
            setFooterData(prev => {
                const { elements: newElements } = updateNestedElements(prev.content, colElementPath, (arr, idx) => {
                    arr.splice(idx, 1);
                });
                setIsFooterDirty(true);
                setHasUnpublishedChanges(true);
                return { ...prev, content: newElements, has_changes: true };
            });
            if (String(selectedElementRef.current.id) === String(colElementPath)) {
                setSelectedElement({ type: 'footer', id: 'footer', sectionId: 'footer', data: null });
            }
            return true;
        }

        if (sectionId === 'header') {
            setHeaderData(prev => {
                const { elements: newElements } = updateNestedElements(prev.content, colElementPath, (arr, idx) => {
                    arr.splice(idx, 1);
                });
                setIsHeaderDirty(true);
                setHasUnpublishedChanges(true);
                return { ...prev, content: newElements, has_changes: true };
            });
            if (String(selectedElementRef.current.id) === String(colElementPath)) {
                setSelectedElement({ type: 'header', id: 'header', sectionId: 'header', data: null });
            }
            return true;
        }

        setPageData(prev => {
            const newSections = prev.sections.map(section => {
                if (section.id !== sectionId) return section;

                const { elements: newElements } = updateNestedElements(section.elements, colElementPath, (arr, idx) => {
                    arr.splice(idx, 1);
                });

                return { ...section, elements: newElements, has_changes: true };
            });
            return { ...prev, sections: newSections };
        });

        if (String(selectedElementRef.current.id) === String(colElementPath)) {
            setSelectedElement({ type: 'section', id: sectionId, sectionId: sectionId, data: null });
        }
        return true;
    };

    const moveElement = (sectionId, elementPath, direction) => {
        saveHistory();

        let newSelectedPath = null;

        if (sectionId === 'footer') {
            setFooterData(prev => {
                const { elements: newElements } = updateNestedElements(prev.content, elementPath, (arr, idx) => {
                    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
                    if (targetIdx >= 0 && targetIdx < arr.length) {
                        [arr[idx], arr[targetIdx]] = [arr[targetIdx], arr[idx]];
                        const pathArr = String(elementPath).split('-');
                        pathArr[pathArr.length - 1] = targetIdx;
                        newSelectedPath = pathArr.join('-');
                    }
                });
                if (newSelectedPath) {
                    setIsFooterDirty(true);
                    setHasUnpublishedChanges(true);
                    return { ...prev, content: newElements, has_changes: true };
                }
                return prev;
            });
        } else if (sectionId === 'header') {
            setHeaderData(prev => {
                const { elements: newElements } = updateNestedElements(prev.content, elementPath, (arr, idx) => {
                    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
                    if (targetIdx >= 0 && targetIdx < arr.length) {
                        [arr[idx], arr[targetIdx]] = [arr[targetIdx], arr[idx]];
                        const pathArr = String(elementPath).split('-');
                        pathArr[pathArr.length - 1] = targetIdx;
                        newSelectedPath = pathArr.join('-');
                    }
                });
                if (newSelectedPath) {
                    setIsHeaderDirty(true);
                    setHasUnpublishedChanges(true);
                    return { ...prev, content: newElements, has_changes: true };
                }
                return prev;
            });
        } else {
            setPageData(prev => {
                const newSections = prev.sections.map(section => {
                    if (section.id !== sectionId) return section;

                    const { elements: newElements } = updateNestedElements(section.elements, elementPath, (arr, idx) => {
                        const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
                        if (targetIdx >= 0 && targetIdx < arr.length) {
                            [arr[idx], arr[targetIdx]] = [arr[targetIdx], arr[idx]];
                            const pathArr = String(elementPath).split('-');
                            pathArr[pathArr.length - 1] = targetIdx;
                            newSelectedPath = pathArr.join('-');
                        }
                    });

                    if (newSelectedPath) {
                        return { ...section, elements: newElements, has_changes: true };
                    }
                    return section;
                });
                return { ...prev, sections: newSections };
            });
        }

        if (newSelectedPath && selectedElementRef.current && String(selectedElementRef.current.id) === String(elementPath)) {
            setSelectedElement(prev => ({ ...prev, id: newSelectedPath }));
        }
    };

    const moveSection = (sectionId, direction) => {
        saveHistory();
        setPageData(prev => {
            const newSections = [...prev.sections];
            const idx = newSections.findIndex(s => String(s.id) === String(sectionId));
            if (idx === -1) return prev;

            const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
            if (targetIdx >= 0 && targetIdx < newSections.length) {
                [newSections[idx], newSections[targetIdx]] = [newSections[targetIdx], newSections[idx]];
                setHasUnpublishedChanges(true);

                return { ...prev, sections: newSections };
            }
            return prev;
        });
    };

    const reorderSection = (sourceId, targetId) => {
        saveHistory();
        setPageData(prev => {
            const newSections = [...prev.sections];
            const sourceIdx = newSections.findIndex(s => String(s.id) === String(sourceId));
            let targetIdx = newSections.findIndex(s => String(s.id) === String(targetId));

            if (sourceIdx === -1 || targetIdx === -1 || sourceIdx === targetIdx) return prev;

            // If dragging down, removing the source section shifts the target left by 1.
            // To insert BEFORE the target (matching the top border UI), we must adjust for this shift.
            if (sourceIdx < targetIdx) {
                targetIdx--;
            }

            const [movedSection] = newSections.splice(sourceIdx, 1);
            newSections.splice(targetIdx, 0, movedSection);

            setHasUnpublishedChanges(true);

            return { ...prev, sections: newSections };
        });
    };

    const copyToClipboard = useCallback(() => {
        const { type, id, data } = selectedElementRef.current;
        if (!data || type === 'header' || type === 'footer') {
            return;
        }

        let copyData = JSON.parse(JSON.stringify(data));
        setClipboard({ type, data: copyData });
        // showAlert({ title: 'Copied', content: `Copied ${type} to clipboard.`, style: 'success' });   // jeric
    }, [showAlert]);

    const pasteFromClipboard = useCallback(async () => {
        if (!clipboard) {
            showAlert({ title: 'Clipboard Empty', content: 'Nothing to paste.', style: 'warning' });
            return;
        }

        let { type, id, data, sectionId } = selectedElementRef.current;

        if (!type) {
            if (pageDataRef.current?.sections?.length > 0) {
                const lastSection = pageDataRef.current.sections[pageDataRef.current.sections.length - 1];
                type = 'section';
                id = lastSection.id;
                sectionId = lastSection.id;
                data = lastSection;
            } else if (clipboard?.type === 'section') {
                type = 'section';
                id = null;
                sectionId = null;
                data = null;
            }
        }

        if (clipboard.type === 'section') {
            if (type === 'section') {
                saveHistory();
                try {
                    const newSection = {
                        id: 'temp_' + Date.now() + Math.floor(Math.random() * 1000),
                        id_page: activePage.id,
                        template: clipboard.data.template,
                        section_name: `${clipboard.data.section_name || 'Section'} (Copy)`,
                        elements: clipboard.data.elements ? JSON.parse(JSON.stringify(clipboard.data.elements)) : [],
                        settings: clipboard.data.settings ? JSON.parse(JSON.stringify(clipboard.data.settings)) : {},
                        has_changes: true
                    };

                    setPageData(prev => {
                        const newSections = [...prev.sections];
                        const insertIndex = newSections.findIndex(s => s.id === id);
                        if (insertIndex !== -1) {
                            newSections.splice(insertIndex + 1, 0, newSection);
                        } else {
                            newSections.push(newSection);
                        }

                        // Persist the new order to the server (filtering out temporary local sections)
                        const orderedIds = newSections.map(s => s.id).filter(id => !String(id).startsWith('temp_'));
                        if (orderedIds.length > 0) {
                            api.put('/api/cms/sections/reorder', { orderedIds }).catch(err => {
                                console.error('Failed to save section order:', err);
                            });
                        }

                        return { ...prev, sections: newSections };
                    });
                    setHasUnpublishedChanges(true);
                    setIsDirty(true);
                } catch (error) {
                    console.error('Failed to paste section:', error);
                }
            } else {
                showAlert({ title: 'Invalid Paste', content: 'Please select a section to paste a section next to.', style: 'warning' });
            }
        } else if (clipboard.type === 'element' || clipboard.type === 'container' || clipboard.type === 'dynamic_data_grid' || clipboard.type === 'form_container' || clipboard.type === 'form_row' || clipboard.type === 'button' || clipboard.type === 'text' || clipboard.type === 'image' || clipboard.type === 'icon' || clipboard.type === 'video' || clipboard.type === 'richtext' || clipboard.type === 'form_input' || clipboard.type === 'form_textarea' || clipboard.type === 'form_select' || clipboard.type === 'form_checkbox' || clipboard.type === 'form_radio' || clipboard.type === 'form_switch') {
            if (type === 'section') {
                addElement(clipboard.data);
            } else if (type === 'container' || type === 'dynamic_data_grid' || type === 'form_container' || type === 'form_row' || (data && (data.element_type === 'container' || data.element_type === 'dynamic_data_grid'))) {
                let elementToPaste = clipboard.data;
                if (isGridTarget(data) && isGridCard(clipboard.data)) {
                    const ascended = await buildAscendedCard(clipboard.data, data);
                    if (ascended) elementToPaste = ascended;
                }
                addElement(elementToPaste);
            } else if (type === 'element' || type === 'button' || type === 'text' || type === 'image' || type === 'icon' || type === 'video' || type === 'richtext' || type === 'form_input' || type === 'form_textarea' || type === 'form_select' || type === 'form_checkbox' || type === 'form_radio' || type === 'form_switch') {
                saveHistory();
                let currentSectionId = sectionId;
                let currentContainerPath = String(id).split('-').slice(0, -1).join('-');
                let pasteIndex = parseInt(String(id).split('-').pop()) + 1;

                const processPaste = (prevData, setIsDirtyFlag) => {
                    let newElements = [...(prevData.content || prevData.elements || [])];
                    if (currentContainerPath) {
                        const { elements: res } = updateNestedElements(newElements, currentContainerPath, (arr, idx) => {
                            const container = { ...arr[idx] };
                            const newElementsArr = [...(container.elements || [])];
                            newElementsArr.splice(pasteIndex, 0, JSON.parse(JSON.stringify(clipboard.data)));
                            container.elements = newElementsArr;
                            arr[idx] = container;
                            return container;
                        });
                        newElements = res;
                    } else {
                        newElements.splice(pasteIndex, 0, JSON.parse(JSON.stringify(clipboard.data)));
                    }
                    if (setIsDirtyFlag) setIsDirtyFlag(true);
                    return { ...prevData, [prevData.content ? 'content' : 'elements']: newElements, has_changes: true };
                };

                if (currentSectionId === 'footer') {
                    setFooterData(prev => processPaste(prev, setIsFooterDirty));
                } else if (currentSectionId === 'header') {
                    setHeaderData(prev => processPaste(prev, setIsHeaderDirty));
                } else {
                    setPageData(prev => {
                        const newSections = prev.sections.map(section => {
                            if (section.id !== currentSectionId) return section;
                            return processPaste(section, null);
                        });
                        return { ...prev, sections: newSections };
                    });
                }
                setHasUnpublishedChanges(true);
                setIsDirty(true);
            } else {
                showAlert({ title: 'Invalid Paste', content: 'Select a section or block to paste this element.', style: 'warning' });
            }
        }
    }, [clipboard, activePage, addElement, saveHistory, showAlert, setHasUnpublishedChanges, setIsDirty]);

    const duplicateElement = useCallback(async () => {
        const { type, id, data, sectionId } = selectedElementRef.current;
        if (!data || type === 'header' || type === 'footer') {
            return;
        }

        let elementToDuplicate = JSON.parse(JSON.stringify(data));

        if (type === 'section') {
            saveHistory();
            try {
                const newSection = {
                    id: 'temp_' + Date.now() + Math.floor(Math.random() * 1000),
                    id_page: activePage.id,
                    template: elementToDuplicate.template,
                    section_name: `${elementToDuplicate.section_name || 'Section'} (Copy)`,
                    elements: elementToDuplicate.elements ? JSON.parse(JSON.stringify(elementToDuplicate.elements)) : [],
                    settings: elementToDuplicate.settings ? JSON.parse(JSON.stringify(elementToDuplicate.settings)) : {},
                    has_changes: true
                };

                setPageData(prev => {
                    const newSections = [...prev.sections];
                    const insertIndex = newSections.findIndex(s => s.id === id);
                    if (insertIndex !== -1) {
                        newSections.splice(insertIndex + 1, 0, newSection);
                    } else {
                        newSections.push(newSection);
                    }

                    const orderedIds = newSections.map(s => s.id).filter(sId => !String(sId).startsWith('temp_'));
                    if (orderedIds.length > 0) {
                        api.put('/api/cms/sections/reorder', { orderedIds }).catch(err => {
                            console.error('Failed to save section order:', err);
                        });
                    }

                    return { ...prev, sections: newSections };
                });
                setHasUnpublishedChanges(true);
                setIsDirty(true);
            } catch (error) {
                console.error('Failed to duplicate section:', error);
            }
        } else if (type === 'element' || type === 'container' || type === 'dynamic_data_grid' || type === 'form_container' || type === 'form_row' || type === 'button' || type === 'text' || type === 'image' || type === 'icon' || type === 'video' || type === 'richtext' || type === 'form_input' || type === 'form_textarea' || type === 'form_select' || type === 'form_checkbox' || type === 'form_radio' || type === 'form_switch') {
            saveHistory();
            let currentSectionId = sectionId;
            let currentContainerPath = String(id).split('-').slice(0, -1).join('-');
            let pasteIndex = parseInt(String(id).split('-').pop()) + 1;

            const processPaste = (prevData, setIsDirtyFlag) => {
                let newElements = [...(prevData.content || prevData.elements || [])];
                if (currentContainerPath) {
                    const { elements: res } = updateNestedElements(newElements, currentContainerPath, (arr, idx) => {
                        const container = { ...arr[idx] };
                        const newElementsArr = [...(container.elements || [])];
                        newElementsArr.splice(pasteIndex, 0, JSON.parse(JSON.stringify(elementToDuplicate)));
                        container.elements = newElementsArr;
                        arr[idx] = container;
                        return container;
                    });
                    newElements = res;
                } else {
                    newElements.splice(pasteIndex, 0, JSON.parse(JSON.stringify(elementToDuplicate)));
                }
                if (setIsDirtyFlag) setIsDirtyFlag(true);
                return { ...prevData, [prevData.content ? 'content' : 'elements']: newElements, has_changes: true };
            };

            if (currentSectionId === 'footer') {
                setFooterData(prev => processPaste(prev, setIsFooterDirty));
            } else if (currentSectionId === 'header') {
                setHeaderData(prev => processPaste(prev, setIsHeaderDirty));
            } else {
                setPageData(prev => {
                    const newSections = prev.sections.map(section => {
                        if (section.id !== currentSectionId) return section;
                        return processPaste(section, null);
                    });
                    return { ...prev, sections: newSections };
                });
            }
            setHasUnpublishedChanges(true);
            setIsDirty(true);
        }
    }, [activePage, saveHistory, setHasUnpublishedChanges, setIsDirty]);

    const addSection = async (templateKey, name) => {
        if (!activePage?.id) {
            showAlert({ title: 'No Page', content: 'Create a page first, then add sections.', style: 'warning' });
            return;
        }
        saveHistory();

        let elements = [];
        let settings = {};

        const template = templates?.sections?.find(t => t.template_key === templateKey);
        if (template) {
            if (template.default_elements) {
                try { elements = typeof template.default_elements === 'string' ? JSON.parse(template.default_elements) : template.default_elements; } catch (e) { }
            }
            if (template.default_settings) {
                try { settings = typeof template.default_settings === 'string' ? JSON.parse(template.default_settings) : template.default_settings; } catch (e) { }
            }
        }

        const newSection = {
            id: 'temp_' + Date.now() + Math.floor(Math.random() * 1000),
            id_page: activePage.id,
            template: templateKey,
            section_name: name,
            elements,
            settings,
            has_changes: true
        };

        setPageData(prev => {
            const newSections = [...prev.sections];
            const selectedId = selectedElementRef.current?.type === 'section'
                ? selectedElementRef.current?.id
                : selectedElementRef.current?.sectionId;

            const insertIndex = newSections.findIndex(s => s.id === selectedId);

            if (insertIndex !== -1) {
                newSections.splice(insertIndex + 1, 0, newSection);
            } else {
                newSections.push(newSection);
            }

            // We do NOT persist the new order to the server yet, because temporary IDs would fail.
            // The order will be saved when "Save Draft" or "Publish" is clicked.
            return {
                ...prev,
                sections: newSections
            };
        });

        setHasUnpublishedChanges(true);
        setIsDirty(true);

        // Auto-select the newly added section
        setSelectedElementState({ type: 'section', id: newSection.id, data: newSection, sectionId: newSection.id });

        // Smooth scroll to the new section if it's not visible
        setTimeout(() => {
            const el = document.querySelector(`.cms-sec-${newSection.id}`);
            if (el) {
                const rect = el.getBoundingClientRect();
                const isViewable = (
                    rect.top >= 0 &&
                    rect.left >= 0 &&
                    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
                    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
                );

                if (!isViewable) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }
        }, 150);
    };

    const removeSection = async (id) => {
        saveHistory();

        if (String(id).startsWith('temp_')) {
            setPageData(prev => ({
                ...prev,
                sections: prev.sections.filter(section => String(section.id) !== String(id))
            }));
            setHasUnpublishedChanges(true);
            setIsDirty(true);
            return;
        }

        try {
            const response = await api.patch(`/api/cms/sections/${id}/delete`);
            if (response.data.success) {
                setPageData(prev => ({
                    ...prev,
                    sections: prev.sections.filter(section => String(section.id) !== String(id))
                }));
            }
        } catch (error) {
            console.error('Failed to remove section:', error);
        }
    };

    const cutToClipboard = useCallback(() => {
        const { type, id, data, sectionId } = selectedElementRef.current;
        if (!data || type === 'header' || type === 'footer') {
            return;
        }

        let copyData = JSON.parse(JSON.stringify(data));
        setClipboard({ type, data: copyData });

        if (type === 'section') {
            removeSection(id);
        } else {
            deleteElement(sectionId, id);
        }
    }, [removeSection, deleteElement]);

    const addPage = async (data) => {
        try {
            const response = await api.post('/api/cms/pages', data);
            if (response.data.success) {
                await fetchPages();
                return response.data.data;
            }
        } catch (error) {
            console.error('Failed to add page:', error);
        }
        return null;
    };

    const updatePage = async (id, data) => {
        try {
            const response = await api.put(`/api/cms/pages/${id}`, data);
            if (response.data.success) {
                await fetchPages();
                if (activePage?.id === id) {
                    await fetchPageData(id);
                }
                return true;
            }
        } catch (error) {
            console.error('Failed to update page:', error);
        }
        return false;
    };

    const deletePage = async (id) => {
        try {
            const response = await api.patch(`/api/cms/pages/${id}/delete`);
            if (response.data.success) {
                await fetchPages();
                return true;
            }
        } catch (error) {
            console.error('Failed to delete page:', error);
        }
        return false;
    };

    const unpublishPage = async (id) => {
        return await updatePage(id, { status: 'draft' });
    };

    const publishPageById = async (id) => {
        try {
            const response = await api.post(`/api/cms/pages/${id}/publish`);
            if (response.data.success) {
                await fetchPages();
                return true;
            }
        } catch (error) {
            console.error('Failed to publish page:', error);
        }
        return false;
    };

    const PageForm = ({ initialData, onSubmit, formId }) => {
        const [formData, setFormData] = useState(initialData);
        const [showCustomCode, setShowCustomCode] = useState(false);

        const handleChange = (e) => {
            const { name, value, type, checked } = e.target;
            setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? (checked ? 1 : 0) : value }));
        };

        return (
            <form id={formId} className='form-case' onSubmit={(e) => { e.preventDefault(); onSubmit(formData); }}>
                <FormInput label="Page Title" placeholder='Page Title' name="title" value={formData.title} onChange={handleChange} required />
                <div className='form-row'>
                    <div className='w4'><FormInput label="Page Slug" name="slug" placeholder='Page Slug' value={formData.slug} onChange={handleChange} required /></div>
                    <div className='w3'><FormSelect label="Page Type" name="type" value={formData.type || '0'} onChange={handleChange} options={[{ value: '0', label: 'Page' }, { value: '1', label: 'Landing' }]} /></div>
                    <div className='w3'><FormSelect label="Status" name="status" value={formData.status} onChange={handleChange} options={[{ value: 'published', label: 'Published' }, { value: 'draft', label: 'Draft' }]} /></div>
                </div>
                <FormInput label="Meta Title" placeholder='Meta Title' name="meta_title" value={formData.meta_title} onChange={handleChange} />
                <FormTextarea label="Meta Description" placeholder='Meta Description' name="meta_desc" value={formData.meta_desc} onChange={handleChange} />
                {formData.type === '1' && (
                    <div style={{ fontSize: '11px', color: '#666', marginTop: '-10px', marginBottom: '15px' }}>
                        Tip: You can use <code style={{ background: '#eee', padding: '2px 4px', borderRadius: '3px' }}>{`{{column_name}}`}</code> syntax to dynamically insert data source values.
                    </div>
                )}

                <div className='form-row'>
                    <div className='w5'>
                        <div className='form-row'>
                            <FormToggle name="has_header" switchClass="small" checked={!!formData.has_header} onChange={handleChange} />
                            <label>Show Header</label>
                        </div>
                    </div>
                    <div className='w5'>
                        <div className='form-row'>
                            <FormToggle name="has_footer" switchClass="small" checked={!!formData.has_footer} onChange={handleChange} />
                            <label>Show Footer</label>
                        </div>
                    </div>
                </div>
                <div className='form-dvr'></div>
                <div className='btn-link' onClick={() => setShowCustomCode(!showCustomCode)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '500' }} >
                    <Icon icon={showCustomCode ? icons.chevronUp : icons.chevronDown} size={16} />
                    <span>Advanced: Custom CSS & JS</span>
                </div>

                {showCustomCode && (
                    <>
                        <FormTextarea
                            label='Custom CSS'
                            placeholder="body { background: #f0f0f0; }"
                            name="custom_css"
                            value={formData.custom_css}
                            onChange={handleChange}
                            rows={10}
                            className="code-editor-wrapper"
                            inputClass="code-editor-textarea"
                        />
                        <FormTextarea
                            label='Custom JS'
                            placeholder="console.log('Hello');"
                            name="custom_js"
                            value={formData.custom_js}
                            onChange={handleChange}
                            rows={10}
                            className="code-editor-wrapper"
                            inputClass="code-editor-textarea"
                        />
                    </>
                )}
            </form>
        );
    };

    const openPageModal = (page = null) => {
        const isEdit = !!page;
        const initialData = isEdit ? {
            title: page.title,
            slug: page.slug,
            type: page.type ?? '0',
            status: page.status,
            meta_title: page.meta_title || '',
            meta_desc: page.meta_desc || '',
            has_header: page.has_header ?? 1,
            has_footer: page.has_footer ?? 1,
            custom_css: page.custom_css || '',
            custom_js: page.custom_js || '',
            id_project: page.id_project
        } : {
            title: '',
            slug: '',
            type: '0',
            status: 'published',
            meta_title: '',
            meta_desc: '',
            has_header: 1,
            has_footer: 1,
            custom_css: '',
            custom_js: '',
            id_project: activeProject || 0
        };

        const handleSubmit = async (formData) => {
            if (!formData.title || !formData.slug) {
                showAlert({ title: 'Validation Error', content: 'Title and Slug are required.', style: 'error' });
                return;
            }

            const result = isEdit ? await updatePage(page.id, formData) : await addPage(formData);
            if (result) {
                closePopup();
            } else {
                showAlert({
                    title: 'Error',
                    content: isEdit ? 'Failed to update page.' : 'Failed to create page. Slug might already exist.',
                    style: 'error'
                });
            }
        };

        openPopup({
            title: isEdit ? `Edit Page: ${page.title}` : 'Add New Page',
            content: <PageForm initialData={initialData} onSubmit={handleSubmit} formId="page_modal_form" />,
            widthClass: 'w-mm',
            actions: [
                { text: 'Cancel', icon: icons.times, onClick: closePopup, className: 'btn-outlined' },
                {
                    text: isEdit ? 'Update' : 'Create',
                    icon: isEdit ? icons.save : icons.add,
                    onClick: () => document.getElementById('page_modal_form')?.requestSubmit()
                }
            ]
        });
    };

    const handleAddPage = () => openPageModal();
    const handleEditPage = (page) => openPageModal(page);

    const confirmDelete = (page) => {
        showAlert({
            title: 'Confirm Delete',
            content: `Are you sure you want to delete the page "${page.title}"? This action cannot be undone.`,
            isAlert: true,
            style: 'error',
            actions: [
                { text: 'Cancel', onClick: closePopup, className: 'btn-outlined' },
                { text: 'Delete', icon: icons.delete, className: 'btn-danger', onClick: async () => { await deletePage(page.id); closePopup(); } }
            ]
        });
    };

    const confirmDraft = (page) => {
        showAlert({
            title: 'Confirm Draft',
            content: `Are you sure you want to move "${page.title}" to drafts? It will no longer be visible to the public.`,
            isAlert: true,
            style: 'warning',
            actions: [
                { text: 'Cancel', onClick: closePopup, className: 'btn-outlined' },
                { text: 'Move to Draft', icon: icons.archive, onClick: async () => { await unpublishPage(page.id); closePopup(); } }
            ]
        });
    };

    const confirmPublish = (page, onConfirm) => {
        showAlert({
            title: 'Confirm Publish',
            content: `Are you sure you want to publish "${page.title}"? It will be visible to the public.`,
            isAlert: true,
            style: 'success',
            actions: [
                { text: 'Cancel', onClick: closePopup, className: 'btn-outlined' },
                { text: 'Move to Publish', icon: icons.send, onClick: async () => { closePopup(); if (onConfirm) await onConfirm(page); } }
            ]
        });
    };

    return (
        <CMSContext.Provider value={{
            pages,
            activePage,
            selectPage,
            pageData,
            setPageData,
            templates,
            loading,
            updateSection,
            addSection,
            removeSection,
            moveSection,
            reorderSection,
            savePageDraft,
            publishPage,
            publishPageById,
            addPage,
            updatePage,
            deletePage,
            unpublishPage,
            refreshPages: fetchPages,
            selectedElement,
            setSelectedElement,
            clipboard,
            copyToClipboard,
            cutToClipboard,
            pasteFromClipboard,
            duplicateElement,
            activeStyleState,
            setActiveStyleState,
            isDirty: isDirty || isFooterDirty || isHeaderDirty || isThemeDirty,
            setIsDirty,
            projects,
            activeProject,
            setActiveProject,
            hasUnpublishedChanges,
            setHasUnpublishedChanges,
            view,
            setView,
            undo,
            redo,
            canUndo: history.length > 0,
            canRedo: future.length > 0,
            updateSectionSetting,
            updateElementContent,
            updateElementProperty,
            updateNavigationProperty,
            addNavigation,
            removeNavigation,
            reorderNavigations,
            addElement,
            deleteElement,
            moveElement,
            addColumnToRow,
            removeColumnFromRow,
            handleAddPage,
            handleEditPage,
            confirmDelete,
            confirmDraft,
            confirmPublish,
            getElementByPath,
            footerData,
            setFooterData,
            isFooterDirty,
            setIsFooterDirty,
            fetchFooterData,
            headerData,
            setHeaderData,
            isHeaderDirty,
            setIsHeaderDirty,
            fetchHeaderData,
            navigations,
            setNavigations,
            fetchNavigations,
            fetchPages,
            applyThemeSettings,
            themeStyles,
            themeSettings,
            setThemeSettings,
            setIsThemeDirty
        }}>
            {children}
        </CMSContext.Provider>
    );
};


